import React, { useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import moment from 'moment';
import {
  Box,
  Chip,
  Popover,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import CakeIcon from '@mui/icons-material/Cake';
import {
  TIPO_COLOR,
  TIPO_LABEL,
  formatDayCellGroupLabel,
  formatGroupedFeriadoTooltip,
  getGroupedLocalidades,
  groupDayFeriados,
  tipoChipSx,
} from './dateUtils';

const MAX_VISIBLE_TAGS = 2;
const CELL_HEIGHT_MONTH = 88;
const CELL_HEIGHT_WEEK = 120;

const stripAniversarioPrefix = (nome) => {
  if (!nome) return '';
  return String(nome).replace(/^anivers[aá]rio\s+de\s+/i, '').trim() || nome;
};

const DayOffChip = ({ group, showLocalidade }) => {
  const names = Array.from(
    new Set((group.items || []).map((i) => stripAniversarioPrefix(i.nome)).filter(Boolean))
  );
  const label = names.length > 1
    ? `${names[0]} · +${names.length - 1}`
    : (names[0] || stripAniversarioPrefix(group.nome) || formatDayCellGroupLabel(group));
  const title = names.length > 1
    ? names.join(', ')
    : formatGroupedFeriadoTooltip(group, { showLocalidade });
  return (
    <Tooltip title={title} enterDelay={400}>
      <Chip
        size="small"
        icon={(
          <CakeIcon
            sx={{
              fontSize: '0.85rem !important',
              color: `${TIPO_COLOR.DAY_OFF_ANIVERSARIO.dark} !important`,
            }}
          />
        )}
        label={label}
        sx={{
          height: 18,
          fontSize: '0.62rem',
          maxWidth: '100%',
          justifyContent: 'flex-start',
          ...tipoChipSx('DAY_OFF_ANIVERSARIO'),
          '& .MuiChip-label': {
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            px: 0.5,
          },
          '& .MuiChip-icon': {
            marginLeft: '4px',
            marginRight: '-2px',
          },
        }}
      />
    </Tooltip>
  );
};

DayOffChip.propTypes = {
  group: PropTypes.object.isRequired,
  showLocalidade: PropTypes.bool,
};

DayOffChip.defaultProps = {
  showLocalidade: false,
};

const DayCell = ({ day, isCurrentMonth, feriadosDoDia, viewMode, showLocalidade }) => {
  const [anchorEl, setAnchorEl] = useState(null);
  const isToday = day.isSame(moment(), 'day');
  const isWeekend = day.day() === 0 || day.day() === 6;

  const { validGroups, invalidGroups, hasPopoverContent } = useMemo(() => {
    const groups = groupDayFeriados(feriadosDoDia);
    const dayOffs = groups.filter((g) => g.status === 'VALIDO' && g.tipo === 'DAY_OFF_ANIVERSARIO');
    const otherValid = groups.filter((g) => g.status === 'VALIDO' && g.tipo !== 'DAY_OFF_ANIVERSARIO');
    // Day-off compactado (1 bolo) primeiro — densifica e não compete com tags de feriado
    const compactedValid = [
      ...(dayOffs.length
        ? [{
          ...dayOffs[0],
          nome: dayOffs.length === 1
            ? dayOffs[0].nome
            : `${dayOffs.length} aniversários`,
          items: dayOffs.flatMap((g) => g.items),
        }]
        : []),
      ...otherValid,
    ];
    return {
      validGroups: compactedValid,
      invalidGroups: groups.filter((g) => g.status === 'INVALIDO'),
      hasPopoverContent: groups.length > 0,
    };
  }, [feriadosDoDia]);

  const visibleGroups = validGroups.slice(0, MAX_VISIBLE_TAGS);
  const extraGroupCount = validGroups.length - visibleGroups.length;
  const open = Boolean(anchorEl);
  const cellHeight = viewMode === 'week' ? CELL_HEIGHT_WEEK : CELL_HEIGHT_MONTH;

  return (
    <Box
      onClick={(e) => hasPopoverContent && setAnchorEl(e.currentTarget)}
      sx={{
        position: 'relative',
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: 1,
        p: 1,
        height: cellHeight,
        minWidth: 0,
        overflow: 'hidden',
        bgcolor: isWeekend ? 'action.hover' : 'background.paper',
        opacity: isCurrentMonth ? 1 : 0.4,
        cursor: hasPopoverContent ? 'pointer' : 'default',
        display: 'flex',
        flexDirection: 'column',
        gap: 0.5,
        boxSizing: 'border-box',
      }}
    >
      <Box
        sx={{
          alignSelf: 'flex-start',
          flexShrink: 0,
          ...(isToday
            ? {
              width: 24,
              height: 24,
              borderRadius: '50%',
              bgcolor: 'primary.main',
              color: 'primary.contrastText',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }
            : { px: 0.25, py: 0.125 }),
        }}
      >
        <Typography
          variant="caption"
          sx={{
            fontWeight: isToday ? 700 : 600,
            fontSize: '0.8125rem',
            lineHeight: 1,
            fontVariantNumeric: 'tabular-nums',
            color: isToday ? 'inherit' : 'text.primary',
          }}
        >
          {day.format('D')}
        </Typography>
      </Box>

      <Stack spacing={0.25} sx={{ minWidth: 0, overflow: 'hidden', flex: 1 }}>
        {visibleGroups.map((group) => (
          group.tipo === 'DAY_OFF_ANIVERSARIO' ? (
            <DayOffChip
              key={`dayoff|${group.tipo}`}
              group={group}
              showLocalidade={showLocalidade}
            />
          ) : (
            <Tooltip
              key={`${group.nome}|${group.tipo}`}
              title={formatGroupedFeriadoTooltip(group, { showLocalidade })}
              enterDelay={400}
            >
              <Chip
                size="small"
                label={formatDayCellGroupLabel(group)}
                sx={{
                  height: 18,
                  fontSize: '0.62rem',
                  maxWidth: '100%',
                  justifyContent: 'flex-start',
                  ...tipoChipSx(group.tipo),
                  '& .MuiChip-label': {
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    px: 0.75,
                  },
                }}
              />
            </Tooltip>
          )
        ))}
        {extraGroupCount > 0 && (
          <Chip
            size="small"
            variant="outlined"
            label={`+${extraGroupCount}`}
            sx={{ height: 18, fontSize: '0.62rem', alignSelf: 'flex-start' }}
          />
        )}
      </Stack>

      {invalidGroups.length > 0 && (
        <Tooltip
          title={`${formatGroupedFeriadoTooltip(invalidGroups[0], { showLocalidade })} — feriado cancelado, não conta mais como dia não útil`}
        >
          <Box
            sx={{
              position: 'absolute',
              top: 6,
              right: 6,
              width: 8,
              height: 8,
              borderRadius: '50%',
              border: '1.5px dashed',
              borderColor: 'error.main',
            }}
          />
        </Tooltip>
      )}

      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={(e) => {
          e?.stopPropagation?.();
          setAnchorEl(null);
        }}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
      >
        <Box sx={{ p: 2, maxWidth: 340, maxHeight: 360, overflow: 'auto' }}>
          <Typography variant="subtitle2" sx={{ mb: 1.5 }}>
            {day.format('DD/MM/YYYY')}
          </Typography>
          <Stack spacing={1.5}>
            {[...validGroups, ...invalidGroups].map((group) => {
              const localidades = showLocalidade
                ? getGroupedLocalidades(group.items).sort((a, b) => a.localeCompare(b, 'pt-BR'))
                : [];
              const isInvalid = group.status === 'INVALIDO';
              const cityCount = group.items.length;
              const isDayOff = group.tipo === 'DAY_OFF_ANIVERSARIO';
              return (
                <Stack key={`${group.nome}|${group.tipo}|${group.status}`} spacing={0.5}>
                  <Stack direction="row" alignItems="center" spacing={1} flexWrap="wrap" useFlexGap>
                    <Chip
                      size="small"
                      icon={isDayOff && !isInvalid ? <CakeIcon sx={{ fontSize: '0.9rem !important' }} /> : undefined}
                      sx={isInvalid ? {} : tipoChipSx(group.tipo)}
                      label={TIPO_LABEL[group.tipo] || group.tipo}
                    />
                    <Typography
                      variant="body2"
                      sx={{
                        fontWeight: 600,
                        textDecoration: isInvalid ? 'line-through' : 'none',
                      }}
                    >
                      {group.nome}
                    </Typography>
                    {cityCount > 1 && !isDayOff && (
                      <Chip
                        size="small"
                        variant="outlined"
                        label={`${cityCount} cidades`}
                        sx={{ height: 20, fontSize: '0.65rem' }}
                      />
                    )}
                  </Stack>
                  {isDayOff && group.items.length > 1 && (
                    <Stack spacing={0.25} sx={{ pl: 0.5 }}>
                      {group.items.map((item) => (
                        <Typography key={item.id || item.nome} variant="caption" color="text.secondary">
                          {item.nome}
                        </Typography>
                      ))}
                    </Stack>
                  )}
                  {localidades.length === 1 && (
                    <Typography variant="caption" color="text.secondary" sx={{ pl: 0.5 }}>
                      {localidades[0]}
                    </Typography>
                  )}
                  {localidades.length > 1 && (
                    <Stack spacing={0.25} sx={{ pl: 0.5, pt: 0.25 }}>
                      {localidades.map((loc) => (
                        <Typography key={loc} variant="caption" color="text.secondary">
                          {loc}
                        </Typography>
                      ))}
                    </Stack>
                  )}
                </Stack>
              );
            })}
          </Stack>
        </Box>
      </Popover>
    </Box>
  );
};

DayCell.propTypes = {
  day: PropTypes.object.isRequired,
  isCurrentMonth: PropTypes.bool,
  feriadosDoDia: PropTypes.array,
  viewMode: PropTypes.oneOf(['month', 'week']),
  showLocalidade: PropTypes.bool,
};

DayCell.defaultProps = {
  isCurrentMonth: true,
  feriadosDoDia: [],
  viewMode: 'month',
  showLocalidade: false,
};

const WEEK_DAY_LABELS = ['Dom', 'Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb'];

const CalendarGrid = ({ days, referenceDate, feriadosByDate, viewMode, showLocalidade }) => (
  <Box sx={{ minWidth: 0, width: '100%' }}>
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: 'repeat(7, minmax(0, 1fr))',
        gap: 0.75,
        mb: 1,
      }}
    >
      {WEEK_DAY_LABELS.map((label) => (
        <Typography
          key={label}
          variant="caption"
          align="center"
          sx={{ fontWeight: 600, color: 'text.secondary', minWidth: 0 }}
        >
          {label}
        </Typography>
      ))}
    </Box>
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: 'repeat(7, minmax(0, 1fr))',
        gap: 0.75,
      }}
    >
      {days.map((day) => (
        <DayCell
          key={day.format('YYYY-MM-DD')}
          day={day}
          isCurrentMonth={viewMode === 'week' || day.isSame(referenceDate, 'month')}
          feriadosDoDia={feriadosByDate.get(day.format('YYYY-MM-DD')) || []}
          viewMode={viewMode}
          showLocalidade={showLocalidade}
        />
      ))}
    </Box>
  </Box>
);

CalendarGrid.propTypes = {
  days: PropTypes.array.isRequired,
  referenceDate: PropTypes.object.isRequired,
  feriadosByDate: PropTypes.instanceOf(Map).isRequired,
  viewMode: PropTypes.oneOf(['month', 'week']).isRequired,
  showLocalidade: PropTypes.bool,
};

CalendarGrid.defaultProps = {
  showLocalidade: false,
};

export default CalendarGrid;
